// src/viqai/CreationWizard.jsx — v294
// Création guidée d'une tâche Workflow, d'une note ou d'un rendez-vous depuis
// l'espace de travail. VIQ.AI pose les questions une à une, affiche un
// récapitulatif puis enregistre l'élément via le service concerné :
//   • tâche      → POST /api/workflow/tasks
//   • note       → POST /api/crm/notes
//   • rendez-vous → POST /api/agenda/items (type « rdv », synchronisé Workflow)
// La note texte et la note vocale sont séparées. La note vocale utilise un enregistrement MediaRecorder continu, qui ne s’arrête jamais sur les silences et n’est transcrit qu’après l’arrêt explicite.

import { useEffect, useMemo, useRef, useState } from "react";
import { apiPost } from "./api";

// ── Dictée longue ────────────────────────────────────────────────────────────
// 1. Web Speech API en continu, relancée automatiquement quand le navigateur
//    coupe (silence, limite ~60 s) : la dictée dure tant que l'utilisateur ne
//    l'arrête pas.
// 2. Sinon, enregistrement audio par segments de 45 s transcrits côté serveur
//    (/api/voice/transcribe), dans l'ordre, sans limite de durée.
function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("Lecture audio impossible"));
    r.readAsDataURL(blob);
  });
}

export function VoiceDictation({ onAppend, disabled = false }) {
  const [state, setState] = useState("idle"); // idle | listening | recording | transcribing
  const [interim, setInterim] = useState("");
  const [error, setError] = useState("");
  const [seconds, setSeconds] = useState(0);
  const stopRef = useRef(false);
  const recRef = useRef(null);
  const mediaRef = useRef(null);
  const queueRef = useRef(Promise.resolve());
  const lastTextRef = useRef("");

  useEffect(() => {
    if (state !== "listening" && state !== "recording") return undefined;
    const t = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [state]);
  useEffect(() => () => { stopRef.current = true; try { recRef.current?.stop(); } catch (_) {} try { mediaRef.current?.stop(); } catch (_) {} }, []);

  const append = (txt) => {
    const t = String(txt || "").trim();
    if (!t) return;
    lastTextRef.current = (lastTextRef.current + " " + t).slice(-600);
    onAppend?.(t);
  };

  const startSpeech = (SR) => {
    const rec = new SR();
    rec.lang = "fr-FR"; rec.continuous = true; rec.interimResults = true;
    rec.onresult = (ev) => {
      let live = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const r = ev.results[i];
        if (r.isFinal) append(r[0].transcript); else live += r[0].transcript;
      }
      setInterim(live);
    };
    rec.onerror = (ev) => {
      if (ev?.error === "not-allowed" || ev?.error === "service-not-allowed") { stopRef.current = true; setError("Micro refusé : autorisez-le dans le navigateur."); setState("idle"); }
    };
    // Le navigateur coupe régulièrement une dictée continue : on relance tant
    // que l'utilisateur n'a pas cliqué sur « Arrêter ».
    rec.onend = () => { setInterim(""); if (!stopRef.current) { try { rec.start(); } catch (_) { setState("idle"); } } else setState("idle"); };
    recRef.current = rec;
    rec.start();
    setState("listening");
  };

  const startRecorder = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setError("La dictée n'est pas disponible sur cet appareil."); return;
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const type = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"].find(t => MediaRecorder.isTypeSupported?.(t)) || "";
    const segment = () => {
      if (stopRef.current) { stream.getTracks().forEach(t => t.stop()); return; }
      const mr = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
      const chunks = [];
      mr.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunks, { type: mr.mimeType || type || "audio/webm" });
        // Segments transcrits dans l'ordre d'enregistrement.
        queueRef.current = queueRef.current.then(async () => {
          if (!blob.size) return;
          setState(s => (s === "idle" ? "transcribing" : s));
          const r = await apiPost("/api/voice/transcribe", { audio: await blobToDataUrl(blob), prompt: lastTextRef.current }).catch(e => ({ success: false, error: e?.message }));
          if (r?.success) append(r.text); else setError(r?.error || "Un segment n'a pas pu être transcrit.");
          if (stopRef.current) setState("idle");
        });
        segment();
      };
      mediaRef.current = mr;
      mr.start();
      setTimeout(() => { if (mr.state === "recording") mr.stop(); }, 45000);
    };
    setState("recording");
    segment();
  };

  const start = async () => {
    setError(""); setSeconds(0); stopRef.current = false;
    const SR = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
    try {
      if (SR) startSpeech(SR); else await startRecorder();
    } catch (e) { setError(e?.message || "Micro indisponible."); setState("idle"); }
  };
  const stop = () => {
    stopRef.current = true;
    try { recRef.current?.stop(); } catch (_) {}
    try { if (mediaRef.current?.state === "recording") mediaRef.current.stop(); } catch (_) {}
    if (state === "recording") setState("transcribing"); else setState("idle");
  };

  const active = state === "listening" || state === "recording";
  const mm = String(Math.floor(seconds / 60)).padStart(2, "0"), ss = String(seconds % 60).padStart(2, "0");
  return (
    <div className="viq-voice-dictation">
      <button type="button" className={`viq-voice-btn${active ? " is-on" : ""}`} onClick={active ? stop : start} disabled={disabled || state === "transcribing"} aria-pressed={active}>
        <span aria-hidden="true">{active ? "■" : "🎙"}</span>
        {active ? `Arrêter la dictée · ${mm}:${ss}` : state === "transcribing" ? "Transcription…" : "Dicter une note vocale"}
      </button>
      {interim && <span className="viq-voice-interim">{interim}</span>}
      {error && <span className="viq-voice-error" role="alert">{error}</span>}
    </div>
  );
}


// ── Note vocale persistante ─────────────────────────────────────────────────
// Contrairement à la Web Speech API, MediaRecorder ne s'arrête pas lors d'un
// silence. Un seul enregistrement continu est conservé jusqu'au clic explicite
// sur « Arrêter », puis transcrit une seule fois : aucune pause ne peut donc
// remplacer ou écraser un morceau du transcript déjà produit.
export function PersistentVoiceRecorder({ transcript = "", onTranscript, disabled = false, autoStart = false }) {
  const [state, setState] = useState("idle"); // idle | recording | transcribing | ready
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState("");
  const [audioUrl, setAudioUrl] = useState("");
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const autoStartRef = useRef(false);

  const cleanupStream = () => {
    try { streamRef.current?.getTracks?.().forEach(t => t.stop()); } catch (_) {}
    streamRef.current = null;
  };
  const clearTimer = () => { if (timerRef.current) clearInterval(timerRef.current); timerRef.current = null; };
  useEffect(() => () => {
    clearTimer();
    try { if (recorderRef.current?.state === "recording" || recorderRef.current?.state === "paused") recorderRef.current.stop(); } catch (_) {}
    cleanupStream();
    if (audioUrl) try { URL.revokeObjectURL(audioUrl); } catch (_) {}
  }, [audioUrl]);

  const start = async () => {
    setError(""); setSeconds(0); chunksRef.current = [];
    if (audioUrl) { try { URL.revokeObjectURL(audioUrl); } catch (_) {} setAudioUrl(""); }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setError("L'enregistrement audio n'est pas disponible sur cet appareil."); return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      streamRef.current = stream;
      const type = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"].find(t => MediaRecorder.isTypeSupported?.(t)) || "";
      const mr = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
      recorderRef.current = mr;
      mr.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      mr.onerror = () => { setError("L'enregistrement audio a rencontré une erreur."); setState("idle"); clearTimer(); cleanupStream(); };
      mr.onstop = async () => {
        clearTimer(); cleanupStream();
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || type || "audio/webm" });
        if (!blob.size) { setError("Aucun son n'a été enregistré."); setState("idle"); return; }
        const url = URL.createObjectURL(blob); setAudioUrl(url); setState("transcribing");
        try {
          if (blob.size > 23 * 1024 * 1024) throw new Error("L'enregistrement est trop volumineux. Enregistrez une note plus courte puis créez une seconde note si nécessaire.");
          const r = await apiPost("/api/voice/transcribe", { audio: await blobToDataUrl(blob), prompt: String(transcript || "").slice(-600) });
          if (!r?.success) throw new Error(r?.error || "La transcription a échoué.");
          const text = String(r.text || "").trim();
          if (!text) throw new Error("Aucun texte n'a été détecté dans l'enregistrement.");
          onTranscript?.(text);
          setState("ready");
        } catch (e) { setError(e?.message || "La transcription a échoué."); setState("ready"); }
      };
      // timeslice garde les données en mémoire au fil du temps sans jamais
      // arrêter l'enregistrement sur les silences. Les chunks appartiennent
      // tous au même MediaRecorder et sont réunis en un seul fichier final.
      mr.start(1000);
      setState("recording");
      timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
    } catch (e) { setError(e?.message || "Micro indisponible."); setState("idle"); cleanupStream(); }
  };
  const stop = () => {
    if (recorderRef.current?.state !== "recording" && recorderRef.current?.state !== "paused") return;
    setState("transcribing");
    try { recorderRef.current.stop(); } catch (_) { setState("idle"); }
  };

  // v304 — Une note vocale démarre immédiatement après le clic utilisateur
  // sur « Note vocale ». getUserMedia garde son propre dialogue de permission ;
  // aucune question de formulaire ne bloque le démarrage de l'enregistrement.
  useEffect(() => {
    if (!autoStart || autoStartRef.current || disabled || state !== "idle") return;
    autoStartRef.current = true;
    start();
    // Lancement unique à l'ouverture du wizard. Les changements d'état du
    // MediaRecorder ne doivent jamais redéclencher un nouvel enregistrement.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);
  const mm = String(Math.floor(seconds / 60)).padStart(2,"0"), ss = String(seconds % 60).padStart(2,"0");
  return <div className="viq-persistent-voice-note">
    <div className="viq-persistent-voice-controls">
      {state !== "recording" ? <button type="button" className="viq-voice-btn" onClick={start} disabled={disabled || state === "transcribing"}><span aria-hidden="true">🎙</span>{audioUrl ? "Recommencer l'enregistrement" : "Démarrer l'enregistrement continu"}</button>
        : <button type="button" className="viq-voice-btn is-on" onClick={stop}><span aria-hidden="true">■</span>Arrêter et transcrire · {mm}:{ss}</button>}
      {state === "recording" && <span className="viq-persistent-live"><i /> Enregistrement continu — les silences ne l'arrêtent pas</span>}
      {state === "transcribing" && <span className="viq-voice-interim">Transcription de l'enregistrement…</span>}
    </div>
    {audioUrl && <audio className="viq-persistent-audio" controls preload="metadata" src={audioUrl}>Votre navigateur ne permet pas la lecture audio.</audio>}
    {error && <span className="viq-voice-error" role="alert">{error}</span>}
    {transcript && <textarea className="viq-persistent-transcript" rows={7} value={transcript} onChange={e => onTranscript?.(e.target.value)} aria-label="Transcript de la note vocale" />}
  </div>;
}

// ── Questions par type ───────────────────────────────────────────────────────
const TODAY = () => new Date().toISOString().slice(0, 10);
const KINDS = {
  task: {
    label: "tâche Workflow", icon: "✓", service: "Workflow",
    steps: [
      { key: "title", q: "Quel est l'intitulé de la tâche ?", type: "text", required: true, placeholder: "ex. Relancer M. Dupont pour la reprise du 3008" },
      { key: "description", q: "Quels détails ajouter ? (facultatif)", type: "textarea", placeholder: "Contexte, informations utiles, prochaine étape…" },
      { key: "action_type", q: "De quel type d'action s'agit-il ?", type: "select", options: [["task","Tâche"],["call","Appel"],["followup","Relance"],["rdv","Rendez-vous"],["sourcing","Sourcing"],["publish","Publication"]] },
      { key: "priority", q: "Quelle priorité ?", type: "select", options: [["low","Basse"],["normal","Normale"],["high","Haute"],["urgent","Urgente"]] },
      { key: "due_at", q: "Pour quand ? (facultatif)", type: "datetime" },
    ],
  },
  note: {
    label: "note", icon: "✎", service: "Notes & relances",
    steps: [
      { key: "title", q: "Quel titre donner à la note ?", type: "text", required: true, placeholder: "ex. Appel avec Mme Martin" },
      { key: "body", q: "Que faut-il noter ?", type: "textarea", required: true, placeholder: "Contenu de la note…" },
      { key: "action", q: "De quel type d'échange s'agit-il ?", type: "select", options: [["note","Note"],["appel","Appel"],["email","E-mail"],["visite","Visite"],["relance","Relance"],["rdv","Rendez-vous"],["autre","Autre"]] },
      { key: "contact_name", q: "Avec quel contact ? (facultatif)", type: "text", placeholder: "Nom du contact" },
      { key: "follow_up_at", q: "Faut-il prévoir une relance ? (facultatif)", type: "datetime" },
    ],
  },
  voice_note: {
    label: "note vocale", icon: "🎙", service: "Notes & relances",
    steps: [
      { key: "body", q: "Parlez librement : l'enregistrement a démarré et continue pendant les silences jusqu'à votre arrêt explicite.", type: "persistent_voice", required: true },
      { key: "title", q: "Quel titre donner à la note vocale ?", type: "text", required: true, placeholder: "ex. Compte-rendu visite VO" },
      { key: "action", q: "À quel type d'échange rattacher cette note ?", type: "select", options: [["note","Note"],["appel","Appel"],["email","E-mail"],["visite","Visite"],["relance","Relance"],["rdv","Rendez-vous"],["autre","Autre"]] },
      { key: "contact_name", q: "Avec quel contact ? (facultatif)", type: "text", placeholder: "Nom du contact" },
      { key: "follow_up_at", q: "Faut-il prévoir une relance ? (facultatif)", type: "datetime" },
    ],
  },
  rdv: {
    label: "rendez-vous", icon: "📅", service: "Agenda",
    steps: [
      { key: "title", q: "Quel est l'objet du rendez-vous ?", type: "text", required: true, placeholder: "ex. Essai BMW X5 avec M. Leroy" },
      { key: "date", q: "Quel jour ?", type: "date", required: true },
      { key: "time", q: "À quelle heure ?", type: "time", required: true },
      { key: "contact", q: "Avec qui ? (facultatif)", type: "text", placeholder: "Nom, téléphone…" },
      { key: "detail", q: "Lieu ou précisions ? (facultatif)", type: "textarea", placeholder: "Adresse, véhicule concerné, documents à prévoir…" },
      { key: "important", q: "Ce rendez-vous est-il prioritaire ?", type: "select", options: [["no","Non"],["yes","Oui, le signaler comme important"]] },
    ],
  },
};

function initialValues(kind, prefill = {}) {
  const v = { ...prefill };
  if (kind === "rdv") {
    const d = prefill.event_date ? new Date(prefill.event_date) : null;
    if (d && !isNaN(d)) { v.date = d.toISOString().slice(0, 10); v.time = d.toTimeString().slice(0, 5); }
    v.important = prefill.important ? "yes" : "no";
  }
  if (kind === "task") { v.priority = v.priority || "normal"; v.action_type = v.action_type || "task"; }
  if (kind === "note" || kind === "voice_note") { v.action = v.action || "note"; }
  for (const k of ["due_at", "follow_up_at"]) {
    if (v[k] && !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v[k])) { const d = new Date(v[k]); v[k] = isNaN(d) ? "" : new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); }
    else if (v[k]) v[k] = String(v[k]).slice(0, 16);
  }
  return v;
}

function displayValue(step, value) {
  if (!value) return "—";
  if (step.type === "select") return (step.options.find(o => o[0] === value) || [value, value])[1];
  if (step.type === "persistent_voice") return `Enregistrement transcrit · ${String(value).split(/\s+/).filter(Boolean).length} mots`;
  if (step.type === "datetime") { const d = new Date(value); return isNaN(d) ? value : d.toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }); }
  if (step.type === "date") { const d = new Date(`${value}T12:00`); return isNaN(d) ? value : d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }); }
  return String(value);
}

async function createItem(kind, v) {
  if (kind === "task") {
    return apiPost("/api/workflow/tasks", {
      title: v.title, description: v.description || null, priority: v.priority || "normal",
      action_type: v.action_type || "task", due_at: v.due_at ? new Date(v.due_at).toISOString() : null,
      source_type: "viqai", dossier_id: v.dossier_id || null, lead_id: v.lead_id || null,
    });
  }
  if (kind === "note" || kind === "voice_note") {
    return apiPost("/api/crm/notes", {
      title: v.title, body: v.body || "", action: v.action || "note",
      contact_name: v.contact_name || null, phone: v.phone || null,
      follow_up_at: v.follow_up_at ? new Date(v.follow_up_at).toISOString() : null,
      target_type: v.dossier_id ? "dossier" : "libre", target_id: v.dossier_id ? String(v.dossier_id) : null,
    });
  }
  const when = new Date(`${v.date}T${v.time || "09:00"}`);
  const detail = [v.contact ? `Avec : ${v.contact}` : null, v.detail || null].filter(Boolean).join("\n") || null;
  return apiPost("/api/agenda/items", { type: "rdv", title: v.title, detail, eventDate: when.toISOString(), important: v.important === "yes", dossierId: v.dossier_id || null });
}

const openService = (kind) => {
  try {
    if (kind === "task") window.dispatchEvent(new Event("viq-open-workflow-expanded"));
    else window.dispatchEvent(new CustomEvent("viq-navigate", { detail: (kind === "note" || kind === "voice_note") ? "notes" : "agenda" }));
  } catch (_) {}
};

export default function CreationWizard({ data }) {
  const kind = KINDS[data?.kind] ? data.kind : "task";
  const def = KINDS[kind];
  const [values, setValues] = useState(() => initialValues(kind, data?.prefill || {}));
  // Les questions déjà renseignées par la demande sont passées : on commence
  // par la première information manquante.
  const firstMissing = useMemo(() => Math.max(0, def.steps.findIndex(s => s.required && !String(values[s.key] || "").trim())), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [step, setStep] = useState(firstMissing);
  const [status, setStatus] = useState({ state: "editing", message: "" });
  const inputRef = useRef(null);
  const summary = step >= def.steps.length;
  const cur = def.steps[step];

  useEffect(() => { try { inputRef.current?.focus({ preventScroll: true }); } catch (_) {} }, [step]);

  const set = (k, v) => setValues(x => ({ ...x, [k]: v }));
  const valid = !cur || !cur.required || String(values[cur.key] || "").trim();
  const next = () => { if (valid) setStep(s => s + 1); };
  const submit = async () => {
    setStatus({ state: "saving", message: "" });
    const r = await createItem(kind, values).catch(e => ({ success: false, error: e?.message }));
    if (r?.success) {
      setStatus({ state: "done", message: `${def.label.charAt(0).toUpperCase()}${def.label.slice(1)} enregistré${(kind === "note" || kind === "voice_note") ? "e" : kind === "task" ? "e" : ""} dans ${def.service}.` });
      try {
        window.dispatchEvent(new CustomEvent("viq-workspace-item-created", { detail: { kind, id: r.id || r.task?.id || r.note?.id || null } }));
        window.dispatchEvent(new Event("viq-agenda-changed"));
        if (kind === "note" || kind === "voice_note") window.dispatchEvent(new Event("viq-notes-changed"));
        if (kind === "task") window.dispatchEvent(new Event("viq-workflow-changed"));
      } catch (_) {}
    } else setStatus({ state: "error", message: r?.error || "L'enregistrement a échoué. Vérifiez les informations puis réessayez." });
  };
  const onKey = (e) => { if (e.key === "Enter" && cur?.type !== "textarea" && !e.shiftKey) { e.preventDefault(); next(); } };

  if (status.state === "done") {
    return <div className="viq-tool-card viq-wizard is-done">
      <div className="viq-tool-card-head"><span aria-hidden="true">{def.icon}</span><strong>{status.message}</strong></div>
      <div className="viq-wizard-summary">{def.steps.filter(s => values[s.key]).map(s => <div key={s.key}><span>{s.q.replace(/\s*\?.*$/, "").replace(/\s*\(facultatif\)/, "")}</span><strong>{displayValue(s, values[s.key])}</strong></div>)}</div>
      <div className="viq-wizard-actions"><button type="button" className="viq-wizard-secondary" onClick={() => openService(kind)}>Ouvrir {def.service}</button></div>
    </div>;
  }

  return <div className="viq-tool-card viq-wizard" aria-label={`Créer une ${def.label}`}>
    <div className="viq-tool-card-head"><span aria-hidden="true">{def.icon}</span><strong>Nouvelle {def.label}</strong>
      <span className="viq-wizard-progress">{summary ? "Récapitulatif" : `Question ${step + 1} / ${def.steps.length}`}</span></div>
    <div className="viq-wizard-bar"><span style={{ width: `${Math.min(100, (step / def.steps.length) * 100)}%` }} /></div>
    {!summary && <div className="viq-wizard-step">
      <label className="viq-wizard-q" htmlFor={`viq-wz-${kind}-${cur.key}`}>{cur.q}</label>
      {cur.type === "persistent_voice"
        ? <PersistentVoiceRecorder transcript={values[cur.key] || ""} onTranscript={(t) => set(cur.key, t)} disabled={status.state === "saving"} autoStart={kind === "voice_note" && step === 0} />
        : cur.type === "textarea"
          ? <textarea id={`viq-wz-${kind}-${cur.key}`} ref={inputRef} rows={3} value={values[cur.key] || ""} placeholder={cur.placeholder || ""} onChange={e => set(cur.key, e.target.value)} />
        : cur.type === "select"
          ? <select id={`viq-wz-${kind}-${cur.key}`} ref={inputRef} value={values[cur.key] || cur.options[0][0]} onChange={e => set(cur.key, e.target.value)} onKeyDown={onKey}>{cur.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          : <input id={`viq-wz-${kind}-${cur.key}`} ref={inputRef} type={cur.type === "datetime" ? "datetime-local" : cur.type} min={cur.type === "date" ? TODAY() : undefined} value={values[cur.key] || ""} placeholder={cur.placeholder || ""} onChange={e => set(cur.key, e.target.value)} onKeyDown={onKey} />}
    </div>}
    {summary && <div className="viq-wizard-summary">
      {def.steps.map((s, i) => <button type="button" key={s.key} onClick={() => setStep(i)} title="Modifier">
        <span>{s.q.replace(/\s*\?.*$/, "").replace(/\s*\(facultatif\)/, "")}</span><strong>{displayValue(s, values[s.key])}</strong>
      </button>)}
    </div>}
    {status.state === "error" && <div className="viq-wizard-error" role="alert">{status.message}</div>}
    <div className="viq-wizard-actions">
      {step > 0 && <button type="button" className="viq-wizard-secondary" onClick={() => setStep(s => Math.max(0, s - 1))} disabled={status.state === "saving"}>Précédent</button>}
      {!summary && !cur.required && <button type="button" className="viq-wizard-secondary" onClick={() => { set(cur.key, cur.type === "select" ? values[cur.key] : ""); setStep(s => s + 1); }}>Passer</button>}
      {!summary && <button type="button" className="viq-wizard-primary" onClick={next} disabled={!valid}>Suivant</button>}
      {summary && <button type="button" className="viq-wizard-primary" onClick={submit} disabled={status.state === "saving"}>{status.state === "saving" ? "Enregistrement…" : `Créer la ${def.label}`}</button>}
    </div>
  </div>;
}

export const CREATION_KINDS = [
  { id: "create-task", kind: "task", label: "Tâche Workflow", icon: "✓" },
  { id: "create-note", kind: "note", label: "Note", icon: "✎" },
  { id: "create-voice-note", kind: "voice_note", label: "Note vocale", icon: "🎙" },
  { id: "create-rdv", kind: "rdv", label: "Rendez-vous", icon: "📅" },
];
